import type { Disposable } from "@/shared/contracts/lifecycle";

export type HoverAnchorRestoreReason =
  | "initial-layout"
  | "screen-change"
  | "tab-change"
  | "resize"
  | "visibility-visible"
  | "pageshow";

interface HoverAnchorRestoreControllerOptions {
  readonly enabled: boolean;
  readonly restore: (reason: HoverAnchorRestoreReason) => void;
  readonly requestAnimationFrame?: (callback: FrameRequestCallback) => number;
  readonly cancelAnimationFrame?: (handle: number) => void;
  readonly framesToWait?: number;
}

export interface HoverAnchorRestoreController extends Disposable {
  requestRestore(reason: HoverAnchorRestoreReason): void;
}

export function createHoverAnchorRestoreController(
  options: HoverAnchorRestoreControllerOptions,
): HoverAnchorRestoreController {
  const requestFrame =
    options.requestAnimationFrame ?? globalThis.requestAnimationFrame;
  const cancelFrame =
    options.cancelAnimationFrame ?? globalThis.cancelAnimationFrame;
  const framesToWait = Math.max(1, Math.floor(options.framesToWait ?? 2));

  let pendingFrameId: number | null = null;
  let pendingReason: HoverAnchorRestoreReason | null = null;
  let remainingFrames = 0;

  const cancelPendingFrame = (): void => {
    if (pendingFrameId === null) {
      return;
    }
    cancelFrame(pendingFrameId);
    pendingFrameId = null;
  };

  const scheduleNextFrame = (): void => {
    pendingFrameId = requestFrame(() => {
      pendingFrameId = null;
      remainingFrames -= 1;
      if (remainingFrames > 0) {
        scheduleNextFrame();
        return;
      }

      const reason = pendingReason;
      pendingReason = null;
      if (reason !== null) {
        options.restore(reason);
      }
    });
  };

  return {
    requestRestore(reason: HoverAnchorRestoreReason): void {
      if (!options.enabled) {
        return;
      }
      pendingReason = reason;
      if (pendingFrameId !== null) {
        return;
      }
      remainingFrames = framesToWait;
      scheduleNextFrame();
    },

    dispose(): void {
      cancelPendingFrame();
      pendingReason = null;
      remainingFrames = 0;
    },
  };
}
